// src/services/TrustScoreService.ts
// V167.0: Profile-only counts (no PostgREST connections/user_connections - 404 in prod).
// Single canonical file: @/services/TrustScoreService

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { calculateTrustScore } from '@/utils/trustScore';

export interface TrustScoreResult {
  trustScore: number;
  friendsCount: number;
  secondDegreeCount: number;
  connectionsCount: number;
  matchesCount?: number;
  isVerified?: boolean;
}

export interface TrustScoreData {
  currentScore: number;
  connectionCount: number;
  pointsPerFriend: number;
  maxScore: number;
  firstDegreeCount: number;
  secondDegreeCount: number;
}

/** Payload for single-orchestrator trust score update (avoids duplicate/stale calls). */
export interface TrustScoreUpdatePayload {
  friends: number;
  second: number;
  /** Omit to preserve server-side is_verified (billing owns verification). */
  verified?: boolean;
}

/** Reason for update; only 'disconnect' or 'admin' allow count decreases on server. */
export type TrustScoreUpdateReason = 'recalc' | 'disconnect' | 'admin';

export class TrustScoreService {
  private static instance: TrustScoreService;
  /** In-flight dedupe: key -> promise. Same payload = reuse promise, no duplicate invoke. */
  private inFlight = new Map<string, Promise<boolean>>();

  static getInstance(): TrustScoreService {
    if (!TrustScoreService.instance) {
      TrustScoreService.instance = new TrustScoreService();
    }
    return TrustScoreService.instance;
  }

  /** D001: Profile-row math for trust display (self: direct users row; peer: peer_users_for_viewer). */
  private resultFromProfileCounts(userData: Record<string, unknown>): TrustScoreResult {
    const isVerified = userData.is_verified === true;
    const profileFriends = Number(userData.friends_count ?? userData.friend_count ?? 0) || 0;
    const profileConnections = Number(userData.connections_count ?? 0) || 0;
    const finalFriends = profileFriends;
    const finalSecondDegree = Math.max(0, profileConnections - profileFriends);
    const scoreFriends = finalFriends * 10;
    const scoreSecond = finalSecondDegree * 1;
    const scoreVerify = isVerified ? 100 : 0;
    const totalScore = scoreFriends + scoreSecond + scoreVerify;
    return {
      trustScore: totalScore,
      friendsCount: finalFriends,
      secondDegreeCount: finalSecondDegree,
      connectionsCount: profileConnections,
      isVerified,
    };
  }

  /**
   * Raw result shape (trustScore, friendsCount, etc.).
   * Use calculateTrustScoreResult() when you need this shape.
   */
  async calculateTrustScoreResult(userId: string): Promise<TrustScoreResult> {
    if (!userId) {
      console.error('[TrustScore] No userId provided. Returning zero.');
      return this.getZeroResult();
    }

    try {
      console.log(`🧮 [TrustScore] Starting Bulletproof Calc (Result) for: ${userId}`);

      const { data: { user: authUser } } = await supabase.auth.getUser();

      if (authUser?.id && userId !== authUser.id) {
        const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
        const { data: rows, error: rpcErr } = await rpcPeerUsersForViewer(supabase, [userId], { includePrefs: false });
        if (rpcErr) {
          console.error('[TrustScore] D001 peer RPC error:', rpcErr);
          return this.getZeroResult();
        }
        const row = rows?.[0] as Record<string, unknown> | undefined;
        if (!row) {
          return this.getZeroResult();
        }
        const out = this.resultFromProfileCounts(row);
        console.log(`✅ [TrustScore] FINAL (peer RPC): ${out.trustScore}`);
        return out;
      }

      const { data: userAny, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .limit(1)
        .maybeSingle();

      if (userError) {
        console.error('[TrustScore] Critical User Fetch Error:', userError);
      }

      const userData = (userAny || {}) as Record<string, unknown>;
      console.log(`👤 [TrustScore] Profile Data (self): Friends=${userData.friends_count ?? 0}`);

      const out = this.resultFromProfileCounts(userData);
      console.log(`✅ [TrustScore] FINAL (Result): ${out.trustScore} (Friends:${out.friendsCount}, 2nd:${out.secondDegreeCount}, Verified:${out.isVerified})`);
      return out;
    } catch (e) {
      console.error('[TrustScore] UNCAUGHT CRASH:', e);
      return this.getZeroResult();
    }
  }

  /**
   * Returns TrustScoreData (currentScore, firstDegreeCount, etc.).
   * Backward compatible: use currentScore / firstDegreeCount / connectionCount.
   */
  async calculateTrustScore(userId: string): Promise<TrustScoreData> {
    try {
      const result = await this.calculateTrustScoreResult(userId);
      return {
        currentScore: result.trustScore,
        connectionCount: result.connectionsCount,
        pointsPerFriend: 10,
        maxScore: 100,
        firstDegreeCount: result.friendsCount,
        secondDegreeCount: result.secondDegreeCount,
      };
    } catch (e) {
      console.error('[TrustScore] calculateTrustScore failed:', e);
      return this.getZeroData();
    }
  }

  /**
   * Commit stable counts after UI quiet period. Call from single orchestrator only
   * (NetworkDataContext) after 1200ms debounce. Server rejects if commit_id <= current.
   */
  async commitStable(
    userId: string,
    payload: TrustScoreUpdatePayload,
    commitId: number,
    commitSource: string
  ): Promise<boolean> {
    return this.updateUserTrustScoreWithPayload(userId, payload, undefined, commitId, commitSource);
  }

  /**
   * Single path to invoke update-trust-score. Dedupes by payload so duplicate/stale
   * calls (e.g. second=0 before 2nd degree loads) don't overwrite correct values.
   * Prefer commitStable from orchestrator after debounce; this remains for non-stable callers.
   */
  async updateUserTrustScoreOnce(
    userId: string,
    payload: TrustScoreUpdatePayload,
    reason?: TrustScoreUpdateReason
  ): Promise<boolean> {
    const key = `${userId}:${payload.friends}:${payload.second}:${payload.verified === undefined ? 'x' : payload.verified ? 1 : 0}`;
    if (this.inFlight.has(key)) return this.inFlight.get(key)!;

    const p = this.updateUserTrustScoreWithPayload(userId, payload, reason).finally(() => {
      this.inFlight.delete(key);
    });
    this.inFlight.set(key, p);
    return p;
  }

  /** Internal: invoke edge function with raw counts, optional reason, optional commit id/source. */
  private async updateUserTrustScoreWithPayload(
    userId: string,
    payload: TrustScoreUpdatePayload,
    reason?: TrustScoreUpdateReason,
    commitId?: number,
    commitSource?: string
  ): Promise<boolean> {
    try {
      console.log(`💾 [TrustScore] Updating raw counts (DB computes trust_score): friends=${payload.friends}, 2nd=${payload.second}${reason ? ` reason=${reason}` : ''}${commitId != null ? ` commit_id=${commitId}` : ''}`);

      const body: Record<string, unknown> = {
        userId,
        friends_count: payload.friends,
        second_degree_count: payload.second,
      };
      if (payload.verified !== undefined) {
        body.is_verified = payload.verified;
      }
      if (reason) body.reason = reason;
      if (commitId != null) body.commit_id = commitId;
      if (commitSource) body.trust_commit_source = commitSource;

      const { data, error } = await supabase.functions.invoke('update-trust-score', { body });

      const status = (error as any)?.context?.status ?? (error as any)?.status;
      const is403 = status === 403 || ((error as any)?.message && String((error as any).message).includes('403'));
      if (error && is403) {
        if (import.meta.env.DEV) {
          logger.debug?.('[TrustScore] Skipped (403) - cannot update other user from client');
        }
        return false;
      }

      if (error) {
        console.error('❌ [TrustScore] Edge Function Failed:', error);
        return false;
      }

      const skipped = (data as any)?.skipped === true;
      const skipReason = (data as any)?.reason;
      if (skipped && skipReason === 'anti_regression_decrease') {
        if (import.meta.env.DEV) {
          logger.debug?.('[TrustScore] Server skipped regressive update (stale counts)');
        }
        return true;
      }
      if (skipped && skipReason === 'stale_commit') {
        if (import.meta.env.DEV) {
          logger.debug?.('[TrustScore] Server skipped stale commit (older commit_id)');
        }
        return true;
      }

      console.log('✅ [TrustScore] DB Sync Complete.');
      return true;
    } catch (e) {
      console.error('[TrustScore] Update Failed:', e);
      return false;
    }
  }

  /** Recalculates from DB/profile then updates via Once (deduped). Prefer updateUserTrustScoreOnce from orchestrator. */
  async updateUserTrustScore(userId: string): Promise<boolean> {
    try {
      const stats = await this.calculateTrustScoreResult(userId);
      return this.updateUserTrustScoreOnce(userId, {
        friends: stats.friendsCount,
        second: stats.secondDegreeCount,
        verified: stats.isVerified ?? false,
      }, 'recalc');
    } catch (e) {
      console.error('[TrustScore] Update Failed:', e);
      return false;
    }
  }

  private getZeroResult(): TrustScoreResult {
    return { trustScore: 0, friendsCount: 0, secondDegreeCount: 0, connectionsCount: 0, isVerified: false };
  }

  private getZeroData(): TrustScoreData {
    return { currentScore: 0, connectionCount: 0, pointsPerFriend: 10, maxScore: 100, firstDegreeCount: 0, secondDegreeCount: 0 };
  }

  /**
   * Calculate trust score using provided network data from NetworkDataContext.
   * This is the preferred method for UI components that already have connection data.
   */
  async calculateTrustScoreFromNetwork(
    userId: string,
    connections: any[],
    secondDegreeConnections: any[],
    authenticatedClient?: any,
    isVerified?: boolean
  ): Promise<TrustScoreData> {
    try {
      console.log(`🧮 [TrustScore] Calculating from network data for: ${userId}`);

      // Count first-degree friends (FRIEND connections with ACCEPTED status)
      const friendsCount = connections.filter(c => {
        const type = c.connectionType || c.type || '';
        const status = c.status || '';
        return (type === 'FRIEND' || type === 'friend') && status === 'ACCEPTED';
      }).length;

      // Second-degree count from provided data
      const secondDegreeCount = secondDegreeConnections?.length || 0;

      // Total connections count (first + second degree)
      const connectionsCount = friendsCount + secondDegreeCount;

      // Use provided verification status or default to false
      const verified = isVerified ?? false;

      // Calculate trust score using canonical formula
      const trustScore = calculateTrustScore({
        firstDegreeCount: friendsCount,
        secondDegreeCount: secondDegreeCount,
        isVerified: verified
      });

      console.log(`✅ [TrustScore] Network calculation: ${trustScore} (${friendsCount}×10 + ${secondDegreeCount}×1 + ${verified ? 100 : 0})`);

      return {
        currentScore: trustScore,
        connectionCount: connectionsCount,
        pointsPerFriend: 10,
        maxScore: 100,
        firstDegreeCount: friendsCount,
        secondDegreeCount: secondDegreeCount
      };

    } catch (error) {
      console.error('[TrustScore] Error calculating from network:', error);
      return this.getZeroData();
    }
  }

  /**
   * Update user trust score in database using provided network data.
   * This ensures the DB gets updated with accurate, fresh network data.
   */
  async updateUserTrustScoreFromNetwork(
    userId: string,
    connections: any[],
    secondDegreeConnections: any[],
    authenticatedClient?: any,
    isVerified?: boolean
  ): Promise<boolean> {
    if (connections === undefined || secondDegreeConnections === undefined) {
      console.warn('[TrustScore] Blocked DB write: network arrays not loaded yet');
      return false;
    }

    try {
      const trustData = await this.calculateTrustScoreFromNetwork(
        userId,
        connections,
        secondDegreeConnections,
        authenticatedClient,
        isVerified
      );

      // Single path via Once (deduped); server anti-regression rejects stale decreases.
      return this.updateUserTrustScoreOnce(userId, {
        friends: trustData.firstDegreeCount,
        second: trustData.secondDegreeCount,
        verified: isVerified ?? false,
      });
    } catch (error) {
      console.error('[TrustScore] Error updating from network:', error);
      return false;
    }
  }

  static async recalculateAndUpdate(userId: string): Promise<boolean> {
    return trustScoreService.updateUserTrustScore(userId); // goes through Once via updateUserTrustScore
  }
}

export const trustScoreService = TrustScoreService.getInstance();
//if (typeof import.meta !== 'undefined' && import.meta.url) {
//  console.log('[TrustScoreService] LOADED FROM:', import.meta.url);
//}